/**
 * 2D world map (S16) — the middle zone of core-loop.md's three-zone model
 * ("World map | 2D | Automated raid | PixiJS / DOM (new)").
 *
 * SVG rather than PixiJS: the map is a 40x30 grid with a handful of region blobs
 * and PoI markers, all of which need crisp text, hit-testing and a fog overlay.
 * SVG gives all three for free, stays sharp at any size, and adds no dependency
 * (Pixi is not installed; Three.js is, for the two 3D zones). The GDD names
 * "PixiJS / DOM" — this is the DOM branch, taken deliberately.
 *
 * This view replaces S15's region dropdown as the raid launcher, per S16's
 * acceptance criterion. Both surfaces read the same `raidStateStore`, so the
 * compact panel (R) and the map (M) can never disagree about raid state.
 *
 * ─── Salvage Codex pass (2026-09-16) ────────────────────────────────────────
 * This was the last panel still wearing Tailwind slate/amber hex literals
 * while every sibling had moved onto the tokens in `src/styles/app.css`. It
 * now takes colour from those tokens, and carries no hex literal at all —
 * `world-map-ui.test.ts` asserts that, because a hex is how a revert would
 * look and nothing else would notice one.
 *
 * The port runs on TWO mechanisms, deliberately, and the split is not
 * cosmetic:
 *
 *   HTML (header, close button, footer strips) -> token names written inline
 *   into `style.cssText`, exactly as `shop-ui.ts` and `currency-shop-ui.ts`
 *   do. That is also what earns this module its entry in
 *   `styles.tokens.test.ts`'s INLINE_STYLE_SOURCES, which fails the build on a
 *   token name that app.css never declares — the other silent failure mode
 *   here, since an unresolvable custom property drops its whole declaration.
 *
 *   SVG (every map node) -> classes from `styles/world-map.css.ts`, NEVER an
 *   inline hex and never a `var()` in a presentation attribute. A
 *   presentation attribute is not CSS: `setAttribute('fill', 'var(--brass)')`
 *   paints nothing and does not fall back, so the obvious sweep would have
 *   blanked the whole map while every test stayed green. That slice's header
 *   carries the full reasoning, including the `el.style.fill` alternative and
 *   why it lost.
 *
 * Behaviour, DOM structure and every map coordinate are untouched: this pass
 * moved colour, font and radius, plus the panel's padding and max-width onto
 * the 18px / min(94vw, …) the sibling panels already use. Nothing else. That
 * keeps the structural assertions in `world-map-ui.test.ts` meaningful across
 * the change rather than rewritten alongside it.
 */
import {
  loadWorldMap, worldMapStore, type RegionLayout, type PoiEntry, type WorldMapState,
} from '../../state/world-map-state';
import {
  raidStateStore, selectRegion, startRaid, dismissRaidResult, pollRaidStatus, raidLocksWorld,
} from '../../state/raid-state';
import {
  ceil1, chipRow, createActiveRaidView, chipIcon, raidElapsedMs, energyCostBar, focusButton, FOCUS_LABELS, raidTitle, statChip, tierChip, tierClass,
  type ActiveRaidView,
} from './raid-panel';
import { formatDuration } from './raid-extract';
import type { RaidFocusKind, RaidState } from '../../state/raid-state.types';
import { gameStateStore } from '../../state/game-state';
import type { CharacterSheetActive } from '../../state/game-state.types';
import { enterLocation } from '../../state/location-state';
import { closeOtherModals, registerPanel } from './panels';
import { createWindowChrome, windowHeading } from './window-chrome';

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Grid units -> viewBox units. The whole map is drawn in grid space and scaled by the viewBox. */
const CELL = 16;

/**
 * The focus awaiting confirmation, or null when the strip is showing its four
 * buttons. Janeks: *"Before going to a raid - prompt confirmation + cost of
 * it."*
 *
 * Module state rather than a field on `raidStateStore`: that store mirrors the
 * SERVER's raid — its id, its step, its bag — and every field in it came off a
 * response. "Which button did you just press" is neither, it dies with the
 * panel, and putting it there would mean a reducer and a wire type for a
 * question only this strip asks. Same argument, opposite answer, as
 * `home-state`'s `confirmHomeKey`, which is on a store because TWO surfaces
 * (the shop card and the house) had to agree about it.
 */
let pendingFocus: RaidFocusKind | null = null;

/**
 * Tier -> zone class. Risk reads off the status trio (moss / warn / rust)
 * rather than a private green-amber-red ramp, so a palette change reaches the
 * map the same way it reaches a health bar. The fills themselves live in
 * `styles/world-map.css.ts`; see that file for why they cannot live here.
 */
const TIER_CLASS: Record<number, string> = {
  1: 'bv-wm-zone-t1', // safe-ish scrubland
  2: 'bv-wm-zone-t2', // contested
  3: 'bv-wm-zone-t3', // lethal
};

let unsubscribers: Array<() => void> = [];
let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
let rerenderNow: (() => void) | null = null;
/** S17 — invoked after a successful Enter, once `locationStateStore.active` is populated. Owned by whoever mounts this UI (index.ts -> bootstrap.ts). */
let enterLocationCallback: (() => void) | null = null;
let entering = false;

/** Deregisters this panel from `ui/panels.ts` — see `registerPanel`. */
let unregisterPanel: (() => void) | null = null;

export function createWorldMapUI(onEnterLocation: () => void): HTMLElement {
  enterLocationCallback = onEnterLocation;
  // The scrim, the frame, the close cross and the backdrop-click now come from
  // the one shell every window shares (`ui/window-chrome.ts`) — this was the
  // last panel still hand-building its own, and the last one on the old look.
  //
  // Two hazards the shell inherits rather than loses, both of which bit this
  // module before: the frame must stay `position: relative` (an absolutely
  // positioned × anchors to the viewport otherwise, which is how the old raid
  // modal shipped with no way to close it), and the scrim must NOT be
  // `pointer-events: none` or the backdrop-click handler never fires. `.panel`
  // and `.bv-win` both set the first; the shell owns the second.
  const { root, panel, body: footer } = createWindowChrome({
    id: 'world-map-ui',
    bodyId: 'wm-footer',
    // Was 900. The map is the whole point of this window and it shared the
    // width with a 280px side column; the zone count went from 6 to 26 and
    // the labels had nowhere to sit. CSS caps it to the viewport.
    width: 1500,
    label: 'Close map',
    onClose: () => requestCloseWorldMap(),
  });

  const header = document.createElement('div');
  header.className = 'bv-wm-head';
  header.innerHTML = `${windowHeading('World Map')}
    <span class="bv-win-sub">Fog lifts as you explore · click a revealed zone</span>`;

  const mapHost = document.createElement('div');
  mapHost.id = 'wm-canvas';

  // Map and raid options sit side by side; the options stack under the map on
  // a narrow screen (see .bv-wm-body in world-map.css.ts).
  const body = document.createElement('div');
  body.className = 'bv-wm-body';
  footer.classList.add('bv-wm-side');
  panel.insertBefore(header, footer);
  panel.insertBefore(body, footer);
  // The map sits in its own framed stage and the raid options float beside it
  // as a separate plate that overlaps the stage's edge (see world-map.css.ts).
  const stage = document.createElement('div');
  stage.className = 'bv-wm-stage';
  stage.append(mapHost);
  attachMapGestures(stage, mapHost);
  body.append(stage, footer);

  // Escape closes. Registered on the document because focus may sit on the
  // canvas, and removed by `disposeWorldMapUI` so a remount doesn't stack them.
  onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && root.style.display !== 'none') {
      e.preventDefault();
      // Innermost first: backing out of "spend 100 energy on this?" should not
      // also throw away the zone the player just picked. The same ladder the
      // black market carries for the deed, and the reason the shop grew one.
      if (pendingFocus) setPendingFocus(null);
      else requestCloseWorldMap();
    }
  };
  document.addEventListener('keydown', onKeyDown);

  const rerender = () => {
    // Skip while hidden. Raid polls tick every ~2s during a run, and each render
    // tears down and rebuilds the whole SVG — pointless for an invisible panel.
    if (root.style.display === 'none') return;
    root.classList.toggle('bv-wm-raidmode', raidLocksWorld());
    renderMap(mapHost, worldMapStore.get(), raidStateStore.get());
    renderFooter(footer, worldMapStore.get(), raidStateStore.get());
  };
  rerenderNow = rerender;
  unsubscribers = [
    worldMapStore.subscribe(rerender),
    raidStateStore.subscribe(rerender),
  ];


  // One registry instead of the five hand-copied guard chains index.ts
  // used to carry. See ui/panels.ts.
  unregisterPanel?.();
  unregisterPanel = registerPanel({ id: 'world-map', isOpen: isWorldMapUIOpen, close: requestCloseWorldMap });
  return root;
}

export function openWorldMapUI(): void {
  closeOtherModals('world-map');
  const el = document.getElementById('world-map-ui');
  if (el) el.style.display = 'flex';
  void loadWorldMap();
  // `rerender` no-ops while hidden, so paint once now that it's visible.
  rerenderNow?.();
}

/**
 * Is the map showing?
 *
 * Reads the same inline `display` the Escape handler above tests, rather than
 * tracking a second module flag that could disagree with it — `openWorldMapUI`
 * / `hideWorldMapUI` write that property and nothing else does.
 */
export function isWorldMapUIOpen(): boolean {
  const el = document.getElementById('world-map-ui');
  return !!el && el.style.display !== 'none';
}

/**
 * Close the map ON THE PLAYER'S BEHALF — the × , Escape, a click on the scrim,
 * or another window asking for the screen.
 *
 * While a raid is running (or its result is waiting) the map is not a window
 * that was opened, it is where the player IS: they are out in the wasteland and
 * the settlement is not behind it. Closing it would show them a city they are
 * not standing in. So those routes refuse, and the one way out is the raid
 * ending and the player pressing Continue, which is `hideWorldMapUI` called
 * directly by the view that watches the raid.
 */
export function requestCloseWorldMap(): void {
  if (raidLocksWorld()) return;
  hideWorldMapUI();
}

export function hideWorldMapUI(): void {
  const el = document.getElementById('world-map-ui');
  if (el) el.style.display = 'none';
  // Never leave a half-answered question behind: reopening the map must not
  // land the player back on "spend 100 energy?" for a zone they have since
  // stopped thinking about.
  pendingFocus = null;
}

/** Set the pending confirmation and repaint. Exported for its test. */
export function setPendingFocus(focus: RaidFocusKind | null): void {
  pendingFocus = focus;
  rerenderNow?.();
}

export function disposeWorldMapUI(): void {
  pencilDrawnFor = null;
  mapView.scale = 1;
  mapView.cx = Number.NaN;
  mapView.cy = Number.NaN;
  dragMoved = false;
  unregisterPanel?.();
  unregisterPanel = null;
  for (const fn of unsubscribers) fn();
  unsubscribers = [];
  if (onKeyDown) document.removeEventListener('keydown', onKeyDown);
  onKeyDown = null;
  rerenderNow = null;
  enterLocationCallback = null;
  entering = false;
  // Module state outlives the element, and this one is a QUESTION: a base-view
  // remount that inherited it would paint "spend 100 energy on this?" over a
  // freshly opened map, for a zone chosen before the teardown. Caught by the
  // confirmation tests, which pressed a focus button in one case and found the
  // previous case's prompt still on screen.
  pendingFocus = null;
}

/**
 * Called from the render loop (~60 Hz) to poll raid status while a raid is live.
 * `pollRaidStatus` throttles itself to `pollIntervalMs`, so this is a cheap no-op
 * on almost every frame.
 *
 * Deliberately SYNC and returning void: as an `async` function awaited with
 * `.catch()` it allocated two promises per frame — ~120/sec of garbage for a
 * no-op whenever no raid was running.
 *
 * Runs regardless of whether the map is open: a raid resolves on the server's
 * clock, and the HUD should reflect a concluded run even if the player never
 * reopens the map.
 */
export function updateRaidPolling(): void {
  if (raidStateStore.get().status !== 'ACTIVE') return;
  pollRaidStatus().catch(() => {});
}

// ─── zoom and pan ────────────────────────────────────────────────────────────
//
// Janeks: *"On mobile, allow zoom on map, to choose which sector to raid at."*
// Twenty-six named zones do not fit a phone-width map at a readable size, so the
// map pinches, drags and wheels. The view is the SVG's own viewBox: zooming
// changes which window of the grid is shown and nothing else, so every hit
// target, label and marker keeps working untouched.
//
// The view lives at module level because `renderMap` rebuilds the SVG on every
// store change (a raid poll ticks every ~2s) and a view held on the element
// would snap back to fully zoomed out each time.

/** How far the map may be pulled in. Kept low: the painted map is a picture, and past about 2.5x it is just soft. */
const MAX_ZOOM = 2.5;
/** Pixels a pointer may travel before a press stops being a tap. */
const TAP_SLOP = 6;

/** `cx`/`cy` are the centre of the visible window in viewBox units; NaN = centred. */
const mapView = { scale: 1, cx: Number.NaN, cy: Number.NaN };
let dragMoved = false;

/** Was the gesture that just ended a drag or pinch? Taps on zones ask this. */
function tapWasDrag(): boolean {
  return dragMoved;
}

function clampView(gridW: number, gridH: number): { x: number; y: number; w: number; h: number } {
  const W = gridW * CELL;
  const H = gridH * CELL;
  mapView.scale = Math.min(MAX_ZOOM, Math.max(1, mapView.scale));
  const w = W / mapView.scale;
  const h = H / mapView.scale;
  const cx = Number.isFinite(mapView.cx) ? mapView.cx : W / 2;
  const cy = Number.isFinite(mapView.cy) ? mapView.cy : H / 2;
  mapView.cx = Math.min(W - w / 2, Math.max(w / 2, cx));
  mapView.cy = Math.min(H - h / 2, Math.max(h / 2, cy));
  return { x: mapView.cx - w / 2, y: mapView.cy - h / 2, w, h };
}

function viewBoxFor(gridW: number, gridH: number): string {
  const v = clampView(gridW, gridH);
  return `${v.x} ${v.y} ${v.w} ${v.h}`;
}

/** Re-apply the view to the live SVG without rebuilding it. */
function applyView(host: HTMLElement): void {
  const wm = worldMapStore.get();
  const svg = host.querySelector('svg');
  if (svg) svg.setAttribute('viewBox', viewBoxFor(wm.gridW, wm.gridH));
}

/** Zoom by `factor`, keeping the viewBox point under (fx, fy) in 0..1 of the host fixed. */
function zoomAt(host: HTMLElement, factor: number, fx = 0.5, fy = 0.5): void {
  const wm = worldMapStore.get();
  const before = clampView(wm.gridW, wm.gridH);
  const px = before.x + before.w * fx;
  const py = before.y + before.h * fy;
  mapView.scale *= factor;
  const after = clampView(wm.gridW, wm.gridH);
  // Re-centre so (px, py) is still under the same screen fraction.
  mapView.cx = px - after.w * (fx - 0.5);
  mapView.cy = py - after.h * (fy - 0.5);
  applyView(host);
}

/** Back to the whole map. Exported for tests; the on-screen buttons were removed. */
export function resetView(host: HTMLElement): void {
  mapView.scale = 1;
  mapView.cx = Number.NaN;
  mapView.cy = Number.NaN;
  applyView(host);
}

/**
 * Wheel, one-finger drag and two-finger pinch on the map stage.
 *
 * Pointer capture is taken ONLY once a press has travelled past `TAP_SLOP`. A
 * capture taken on pointerdown would redirect the click to the stage and a
 * plain tap on a zone would never reach the zone (the same trap the settlement
 * view's camera drag documents). A press that stays put is left alone and
 * clicks through normally.
 */
function attachMapGestures(stage: HTMLElement, host: HTMLElement): void {
  const pointers = new Map<number, { x: number; y: number }>();
  let pinchDist = 0;
  let startX = 0;
  let startY = 0;

  const rect = (): DOMRect => (host.querySelector('svg') ?? host).getBoundingClientRect();

  stage.addEventListener('wheel', (e) => {
    e.preventDefault();
    const r = rect();
    zoomAt(host, e.deltaY < 0 ? 1.25 : 1 / 1.25,
      (e.clientX - r.left) / Math.max(1, r.width), (e.clientY - r.top) / Math.max(1, r.height));
  }, { passive: false });

  stage.addEventListener('pointerdown', (e) => {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.size === 1) { dragMoved = false; startX = e.clientX; startY = e.clientY; }
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      dragMoved = true;
    }
  });

  stage.addEventListener('pointermove', (e) => {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;
    const cur = { x: e.clientX, y: e.clientY };
    const r = rect();
    if (pointers.size === 2) {
      pointers.set(e.pointerId, cur);
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist > 0 && dist > 0) {
        zoomAt(host, dist / pinchDist,
          ((a.x + b.x) / 2 - r.left) / Math.max(1, r.width), ((a.y + b.y) / 2 - r.top) / Math.max(1, r.height));
      }
      pinchDist = dist;
      return;
    }
    if (pointers.size !== 1) return;
    if (!dragMoved && Math.hypot(cur.x - startX, cur.y - startY) < TAP_SLOP) return;
    if (!dragMoved) {
      dragMoved = true;
      stage.setPointerCapture?.(e.pointerId);
    }
    // Pixels to viewBox units at the CURRENT zoom: the visible window is
    // `W / scale` wide and fills `r.width` pixels.
    const wm = worldMapStore.get();
    const v = clampView(wm.gridW, wm.gridH);
    mapView.cx -= (cur.x - prev.x) * (v.w / Math.max(1, r.width));
    mapView.cy -= (cur.y - prev.y) * (v.h / Math.max(1, r.height));
    pointers.set(e.pointerId, cur);
    applyView(host);
  });

  const end = (e: PointerEvent): void => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchDist = 0;
    if (stage.hasPointerCapture?.(e.pointerId)) stage.releasePointerCapture(e.pointerId);
    // The click that follows a drag fires after pointerup; clear the flag on the
    // next task so that click is still seen as part of the drag and ignored.
    if (pointers.size === 0) setTimeout(() => { dragMoved = false; }, 0);
  };
  stage.addEventListener('pointerup', end);
  stage.addEventListener('pointercancel', end);
}

// ─── rendering ──────────────────────────────────────────────────────────────

function renderMap(host: HTMLElement, wm: WorldMapState, raid: RaidState): void {
  host.innerHTML = '';

  if (wm.status === 'loading' || wm.status === 'idle') {
    host.appendChild(note('Loading map…'));
    return;
  }
  if (wm.status === 'error') {
    host.appendChild(note(wm.error ?? 'Map unavailable'));
    return;
  }

  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', viewBoxFor(wm.gridW, wm.gridH));
  svg.setAttribute('width', '100%');
  svg.setAttribute('role', 'img');
  svg.setAttribute('aria-label', 'World map with fog of war');
  // `setAttribute('class', …)`, not `.className`: on an SVGElement `className`
  // is a read-only SVGAnimatedString, so assigning a string to it is a silent
  // no-op. Every SVG node below sets its class the same way for that reason.
  svg.setAttribute('class', 'bv-wm-svg');

  // Painted ground. Stretched to the viewBox so every region and PoI keeps its
  // grid position; the overlay below is unchanged.
  const ground = document.createElementNS(SVG_NS, 'image');
  ground.setAttribute('href', mapImageUrl(wm.baseImage));
  ground.setAttribute('x', '0');
  ground.setAttribute('y', '0');
  ground.setAttribute('width', String(wm.gridW * CELL));
  ground.setAttribute('height', String(wm.gridH * CELL));
  ground.setAttribute('preserveAspectRatio', 'none');
  svg.appendChild(ground);

  svg.appendChild(gridLayer(wm));
  svg.appendChild(fogLayer(wm));

  // Regions under PoIs so markers stay readable.
  for (const r of wm.regions) svg.appendChild(regionNode(r, raid));
  for (const p of wm.pois) {
    const node = poiNode(p, wm);
    if (node) svg.appendChild(node);
  }

  // A raid in progress draws its own overlay on top.
  if (raid.status === 'ACTIVE') svg.appendChild(raidOverlay(wm, raid));

  host.appendChild(svg);
}

/**
 * Fog of war: the whole painted map is darkened and only the ground the player
 * has explored shows through.
 *
 * Janeks: *"Add fog of war. So that we dont/barely see the rest of the map,
 * until we explored."* A dark layer sits over the painting with a soft hole
 * cut at every revealed zone, done with an SVG MASK rather than by clipping, so
 * the edge feathers and two neighbouring holes merge instead of showing a seam.
 *
 * The home zone (the first by reveal order) is always clear. A brand-new
 * account has revealed nothing, and a map that opens fully black gives the
 * player nothing to read their first raid from.
 *
 * Paint comes from classes (`bv-wm-fog*` in world-map.css.ts), not attributes:
 * an SVG presentation attribute cannot take a design token and this module is
 * held to carrying no colour literal.
 */
function fogLayer(wm: WorldMapState): SVGGElement {
  const g = document.createElementNS(SVG_NS, 'g');
  g.setAttribute('class', 'bv-wm-fog-layer');
  const W = wm.gridW * CELL;
  const H = wm.gridH * CELL;

  const defs = document.createElementNS(SVG_NS, 'defs');
  const grad = document.createElementNS(SVG_NS, 'radialGradient');
  grad.setAttribute('id', 'wm-fog-hole');
  for (const [offset, cls] of [['0', 'bv-wm-fog-in'], ['0.45', 'bv-wm-fog-in'], ['1', 'bv-wm-fog-out']] as const) {
    const stop = document.createElementNS(SVG_NS, 'stop');
    stop.setAttribute('offset', offset);
    stop.setAttribute('class', cls);
    grad.appendChild(stop);
  }
  defs.appendChild(grad);

  const mask = document.createElementNS(SVG_NS, 'mask');
  mask.setAttribute('id', 'wm-fog-mask');
  mask.setAttribute('maskUnits', 'userSpaceOnUse');
  mask.setAttribute('x', '0'); mask.setAttribute('y', '0');
  mask.setAttribute('width', String(W)); mask.setAttribute('height', String(H));
  // White = fog shows. Each hole paints soft black over it.
  const solid = document.createElementNS(SVG_NS, 'rect');
  solid.setAttribute('width', String(W)); solid.setAttribute('height', String(H));
  solid.setAttribute('class', 'bv-wm-fog-white');
  mask.appendChild(solid);
  const home = wm.regions.reduce<RegionLayout | null>((best, r) => (!best || r.order < best.order ? r : best), null);
  for (const r of wm.regions) {
    if (!r.revealed && r !== home) continue;
    const hole = document.createElementNS(SVG_NS, 'circle');
    hole.setAttribute('cx', String(r.cx * CELL));
    hole.setAttribute('cy', String(r.cy * CELL));
    hole.setAttribute('r', String((r.r * 2.2 + 3) * CELL));
    hole.setAttribute('class', 'bv-wm-fog-hole');
    mask.appendChild(hole);
  }
  defs.appendChild(mask);
  g.appendChild(defs);

  const veil = document.createElementNS(SVG_NS, 'rect');
  veil.setAttribute('width', String(W)); veil.setAttribute('height', String(H));
  veil.setAttribute('mask', 'url(#wm-fog-mask)');
  veil.setAttribute('class', 'bv-wm-fog');
  g.appendChild(veil);
  return g;
}

/** The zone whose pencil circle was last drawn, so a re-render does not redraw it. */
let pencilDrawnFor: string | null = null;

/**
 * A hand-drawn pencil circle around the chosen raid zone.
 *
 * An image, not a CSS ring: Janeks asked for a circle that looks drawn, and a
 * stroked SVG circle is exactly the "flat css circle" he ruled out. The art is
 * a loose red pencil loop with overlapping ends, sized to sit just outside the
 * zone's dot and turned a little per zone so two picks never look stamped.
 *
 * When the selection changes it is DRAWN: a mask with one stroked circle whose
 * dash offset runs from empty to full reveals the image around the loop, in
 * step with the pencil sound (`createPencilSound`). When the same zone is simply
 * re-rendered (a raid poll rebuilds the whole SVG every couple of seconds) the
 * mask starts complete, so it does not keep re-drawing.
 */
function pencilCircle(r: RegionLayout): SVGGElement {
  const g = document.createElementNS(SVG_NS, 'g');
  g.setAttribute('class', 'bv-wm-pencil');
  const cx = r.cx * CELL;
  const cy = r.cy * CELL;
  const size = r.r * CELL * 2 + 46;
  // Stable per zone, not random: the same zone always gets the same tilt.
  let h = 0;
  for (const ch of r.key) h = (h * 31 + ch.charCodeAt(0)) % 360;
  const tilt = (h % 17) - 8;
  const animate = pencilDrawnFor !== r.key;
  pencilDrawnFor = r.key;

  const maskId = 'wm-pencil-mask';
  const defs = document.createElementNS(SVG_NS, 'defs');
  const mask = document.createElementNS(SVG_NS, 'mask');
  mask.setAttribute('id', maskId);
  mask.setAttribute('maskUnits', 'userSpaceOnUse');
  mask.setAttribute('x', String(cx - size)); mask.setAttribute('y', String(cy - size));
  mask.setAttribute('width', String(size * 2)); mask.setAttribute('height', String(size * 2));
  const sweep = document.createElementNS(SVG_NS, 'circle');
  sweep.setAttribute('cx', String(cx)); sweep.setAttribute('cy', String(cy));
  sweep.setAttribute('r', String(size * 0.42));
  sweep.setAttribute('pathLength', '1');
  // Starts at twelve o'clock and runs clockwise, like a right-handed circle.
  sweep.setAttribute('transform', `rotate(-90 ${cx} ${cy})`);
  sweep.setAttribute('class', `bv-wm-pencil-sweep${animate ? ' is-drawing' : ''}`);
  // A style property, not an attribute: this is per-zone geometry, and the
  // paint-attribute rule is about colour.
  sweep.style.strokeWidth = String(size * 0.3);
  mask.appendChild(sweep);
  defs.appendChild(mask);
  g.appendChild(defs);

  const img = document.createElementNS(SVG_NS, 'image');
  img.setAttribute('href', '/icons/ui/pencil-circle.webp');
  img.setAttribute('x', String(cx - size / 2)); img.setAttribute('y', String(cy - size / 2));
  img.setAttribute('width', String(size)); img.setAttribute('height', String(size));
  img.setAttribute('transform', `rotate(${tilt} ${cx} ${cy})`);
  img.setAttribute('mask', `url(#${maskId})`);
  img.setAttribute('class', 'bv-wm-pencil-img');
  g.appendChild(img);
  return g;
}

const DEFAULT_MAP_IMAGE = 'world_map.webp';
/** Bumped when the painted map is redrawn, so a cached copy of the old art is not served. */
const MAP_ART_VERSION = 2;

/** Seeded rows predate the painted map and still say world_map.png. */
function mapImageUrl(baseImage: string | undefined): string {
  const file = baseImage && baseImage.endsWith('.webp') ? baseImage : DEFAULT_MAP_IMAGE;
  return `/maps/${file}?v=${MAP_ART_VERSION}`;
}

function gridLayer(wm: WorldMapState): SVGGElement {
  const g = document.createElementNS(SVG_NS, 'g');
  // Stroke lives on the group: SVG stroke properties inherit, so one class
  // paints every line without touching them individually.
  g.setAttribute('class', 'bv-wm-grid');
  for (let x = 0; x <= wm.gridW; x += 5) {
    const l = document.createElementNS(SVG_NS, 'line');
    l.setAttribute('x1', String(x * CELL)); l.setAttribute('y1', '0');
    l.setAttribute('x2', String(x * CELL)); l.setAttribute('y2', String(wm.gridH * CELL));
    g.appendChild(l);
  }
  for (let y = 0; y <= wm.gridH; y += 5) {
    const l = document.createElementNS(SVG_NS, 'line');
    l.setAttribute('x1', '0'); l.setAttribute('y1', String(y * CELL));
    l.setAttribute('x2', String(wm.gridW * CELL)); l.setAttribute('y2', String(y * CELL));
    g.appendChild(l);
  }
  return g;
}

function regionNode(r: RegionLayout, raid: RaidState): SVGGElement {
  const g = document.createElementNS(SVG_NS, 'g');
  const isRaidTarget = raidLocksWorld(raid) && raid.districtId === r.key;
  // While a raid owns the map the red pencil circle marks ITS zone, whatever
  // was picked before; otherwise it marks the player's pick.
  const selected = raidLocksWorld(raid) ? isRaidTarget : raidStateStore.get().selectedRegion === r.key;
  // Selectable whether or not it's revealed. Raid start does NOT gate on
  // discovery (`requireRegionDiscovered` defaults off) and `PlayerDiscovery`
  // starts empty, so restricting this to revealed regions left a brand-new
  // account with nothing to pick and no way to raid.
  //
  // Still blocked during an ACTIVE raid: rule 2's snapshot is immutable, so
  // retargeting a run in flight is meaningless.
  // The raid result stays up until Continue, so the lock outlasts ACTIVE: the
  // player cannot pick a new zone while the old run is still on screen.
  const locked = raidLocksWorld(raid);
  const clickable = !locked;

  const circle = document.createElementNS(SVG_NS, 'circle');
  circle.setAttribute('cx', String(r.cx * CELL));
  circle.setAttribute('cy', String(r.cy * CELL));
  // GEOMETRY is an attribute; PAINT is a class. The two halves are separated
  // on purpose, and the separation is load-bearing in both directions:
  //
  //  - `r` MUST be set here. It is derived per region from `cells` and is not
  //    something a stylesheet can know. The Codex restyle moved fill, stroke
  //    and opacity onto classes and dropped this line with them — every zone
  //    kept a perfectly correct colour and rendered nothing at all, because an
  //    SVG circle with no radius has no area. No test saw it: they assert
  //    structure and class names, and both were right. It was found by taking
  //    a screenshot and reading the computed style back out of the DOM.
  //  - Paint must NOT be set here. Mixing the two (a class for fill, an
  //    attribute for fill-opacity) is the half-migration that leaves a shape
  //    looking right in one state and blank in another.
  circle.setAttribute('r', String(r.r * CELL));
  const zoneClasses = ['bv-wm-zone'];
  // `?? 'bv-wm-zone-tx'`: a tier outside 1..3 still needs a fill, or the
  // circle inherits SVG's default black and reads as a hole in the map.
  zoneClasses.push(r.revealed ? (TIER_CLASS[r.tier] ?? 'bv-wm-zone-tx') : 'bv-wm-zone-fog');
  if (r.positionEstimated) zoneClasses.push('bv-wm-zone-est');
  if (selected) zoneClasses.push('is-selected');
  if (isRaidTarget) zoneClasses.push('is-target');
  circle.setAttribute('class', zoneClasses.join(' '));
  g.appendChild(circle);
  // The chosen zone is circled in pencil, not outlined: see `pencilCircle`.
  if (selected) g.appendChild(pencilCircle(r));

  if (r.revealed) {
    const label = document.createElementNS(SVG_NS, 'text');
    // Names sit BELOW the circle, not inside it: the circles are small dots now
    // and a name wider than its dot has nothing to be centred in.
    label.setAttribute('x', String(r.cx * CELL));
    label.setAttribute('y', String((r.cy + r.r) * CELL + 20));
    label.setAttribute('text-anchor', 'middle');
    label.setAttribute('class', 'bv-wm-label');
    label.textContent = r.label;
    g.appendChild(label);

    const sub = document.createElementNS(SVG_NS, 'text');
    sub.setAttribute('x', String(r.cx * CELL));
    sub.setAttribute('y', String((r.cy + r.r) * CELL + 37));
    sub.setAttribute('text-anchor', 'middle');
    sub.setAttribute('class', 'bv-wm-sub');
    sub.textContent = `tier ${r.tier} · ${r.discoveredPoiCount}/${r.poiCount} found`;
    g.appendChild(sub);
  } else {
    // Unscouted: the circle keeps its NAME, but nothing inside it — no PoIs and
    // no found count. Janeks asked for the circles to be named, and a map of
    // twenty-six anonymous grey discs gives nobody a reason to go anywhere.
    // The tier IS shown too, because the player is allowed to raid here and
    // needs to know the risk before committing.
    const q = document.createElementNS(SVG_NS, 'text');
    q.setAttribute('x', String(r.cx * CELL));
    q.setAttribute('y', String((r.cy + r.r) * CELL + 20));
    q.setAttribute('text-anchor', 'middle');
    q.setAttribute('class', 'bv-wm-fog-label');
    q.textContent = r.label;
    g.appendChild(q);

    const t = document.createElementNS(SVG_NS, 'text');
    t.setAttribute('x', String(r.cx * CELL));
    t.setAttribute('y', String((r.cy + r.r) * CELL + 37));
    t.setAttribute('text-anchor', 'middle');
    t.setAttribute('class', 'bv-wm-fog-tier');
    t.textContent = `unscouted · tier ${r.tier}`;
    g.appendChild(t);
  }

  if (clickable) {
    // `classList.add`, not `setAttribute('class', …)`: the latter REPLACES.
    // The group carries no other class today, so both work — but a class added
    // to it later would vanish without a word, which is the same class of
    // silent loss this whole restyle was written to avoid.
    g.classList.add('bv-wm-hit');
    g.addEventListener('click', () => { if (!tapWasDrag()) selectRegion(r.key); });
    // Keyboard reachability: an SVG group isn't focusable by default.
    g.setAttribute('tabindex', '0');
    g.setAttribute('role', 'button');
    // Distinguishes a region's button from a Location PoI's own (S17) —
    // both carry `role="button"`, but only region selection is disabled
    // during an ACTIVE raid (Location entry is a separate activity).
    g.dataset.kind = 'region';
    g.setAttribute('aria-label', r.revealed
      ? `Select ${r.label}, tier ${r.tier}`
      : `Select ${r.label}, unscouted, tier ${r.tier}`);
    g.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') { e.preventDefault(); selectRegion(r.key); }
    });
  }
  return g;
}

/** Sites whose marker is not the default for their kind. */
const POI_ICON_BY_KEY: Record<string, 'bounty' | 'quest' | 'loot'> = {
  brawl: 'bounty',
  rival_settlement: 'bounty',
};

/**
 * Draws a PoI. Undiscovered PoIs in unrevealed regions are omitted entirely —
 * rendering them dimmed would leak the map the fog exists to hide.
 */
function poiNode(p: PoiEntry, wm: WorldMapState): SVGGElement | null {
  const regionRevealed = wm.revealedRegions.includes(p.region);
  if (!regionRevealed) return null;

  const g = document.createElementNS(SVG_NS, 'g');
  const x = p.gridX * CELL;
  const y = p.gridY * CELL;
  // Marker half-size follows the grid so a site is the same share of the map on
  // the 80-column grid as it was on the old 40-column one.
  const m = 5.5 * (wm.gridW / 40);
  // Discovered markers glow brass; undiscovered ones stay the dimmest text
  // token. Same class on both shapes, so the diamond/square distinction stays
  // purely about geometry.
  const markerClass = `bv-wm-poi${p.discovered ? ' is-found' : ''}`;

  if (p.kind === 'location') {
    // Diamond = enterable 3D Location (S17's dive target).
    const d = document.createElementNS(SVG_NS, 'polygon');
    d.setAttribute('points', `${x},${y - m} ${x + m},${y} ${x},${y + m} ${x - m},${y}`);
    d.setAttribute('class', markerClass);
    g.appendChild(d);
  } else {
    // Square = resolved abstractly on the map.
    const s = document.createElementNS(SVG_NS, 'rect');
    const half = m * 0.8;
    s.setAttribute('x', String(x - half)); s.setAttribute('y', String(y - half));
    s.setAttribute('width', String(half * 2)); s.setAttribute('height', String(half * 2));
    s.setAttribute('class', markerClass);
    g.appendChild(s);
  }

  // The glyph on the marker says what kind of place it is. PoIs carry no
  // "bounty" or "quest" category in the data, so the picture follows the kind
  // (map-resolved sites are loot crates, enterable locations a question mark to
  // go and find out) with a short per-key override for the sites that are known
  // to be about something else. Adding a category later means editing this table.
  const icon = POI_ICON_BY_KEY[p.key] ?? (p.kind === 'location' ? 'quest' : 'loot');
  const img = document.createElementNS(SVG_NS, 'image');
  const iconSize = m * 2.5;
  img.setAttribute('href', `/ui/map/poi-${icon}.png`);
  img.setAttribute('x', String(x - iconSize / 2)); img.setAttribute('y', String(y - iconSize / 2));
  img.setAttribute('width', String(iconSize)); img.setAttribute('height', String(iconSize));
  img.setAttribute('class', `bv-wm-poi-icon${p.discovered ? ' is-found' : ''}`);
  g.appendChild(img);

  const title = document.createElementNS(SVG_NS, 'title');
  title.textContent = `${p.key}${p.isTutorial ? ' (tutorial)' : ''} — ${p.kind === 'location' ? 'enterable' : 'map-resolved'}`
    + (p.discovered ? '' : ' — undiscovered');
  g.appendChild(title);

  // S17 — a discovered Location PoI is a direct-visit target, entirely
  // independent of Raid (rule 4: "a targeted PoI visit, lighter than a raid").
  // Undiscovered PoIs are inert here too — the server rejects POI_NOT_DISCOVERED
  // regardless, but there is no point firing a request that can only 409.
  if (p.kind === 'location' && p.discovered) {
    // Additive, for the reason spelled out in `regionNode`.
    g.classList.add('bv-wm-hit');
    const attempt = () => {
      if (entering) return;
      entering = true;
      void enterLocation(p.id).then((ok) => {
        entering = false;
        if (!ok) return;
        hideWorldMapUI();
        enterLocationCallback?.();
      });
    };
    g.addEventListener('click', () => { if (!tapWasDrag()) attempt(); });
    g.setAttribute('tabindex', '0');
    g.setAttribute('role', 'button');
    g.dataset.kind = 'poi';
    g.setAttribute('aria-label', `Enter ${p.key}`);
    g.addEventListener('keydown', (e) => {
      const k = (e as KeyboardEvent).key;
      if (k === 'Enter' || k === ' ') { e.preventDefault(); attempt(); }
    });
  }

  return g;
}

/** Labels the region a live raid is running in. The red pencil circle (`pencilCircle`) is the marker. */
function raidOverlay(wm: WorldMapState, raid: RaidState): SVGGElement {
  const g = document.createElementNS(SVG_NS, 'g');
  const region = wm.regions.find((r) => r.key === raid.districtId);
  if (!region) return g;

  const t = document.createElementNS(SVG_NS, 'text');
  t.setAttribute('x', String(region.cx * CELL));
  t.setAttribute('y', String((region.cy - region.r) * CELL - 8));
  t.setAttribute('text-anchor', 'middle');
  t.setAttribute('class', 'bv-wm-raid-label');
  t.textContent = `RAID · ${formatDuration(raidElapsedMs(raid))}`;
  g.appendChild(t);
  return g;
}

// ─── footer: launch / live status / result ───────────────────────────────────

/**
 * The live strip is built once per raid and updated in place — see the header
 * of `raid-panel.ts`. Rebuilding it on every poll restarts the walking video.
 */
let activeView: ActiveRaidView | null = null;

function renderFooter(host: HTMLElement, wm: WorldMapState, raid: RaidState): void {
  if (wm.status === 'ready' && raid.status === 'ACTIVE') {
    if (!activeView || !host.contains(activeView.root)) {
      host.innerHTML = '';
      activeView = createActiveRaidView();
      host.appendChild(activeView.root);
    }
    activeView.update(raid);
    return;
  }
  activeView = null;
  host.innerHTML = '';
  if (wm.status !== 'ready') return;

  // Any finished raid with an outcome, not just the two terminal statuses: the
  // raid screen only ends on Continue, so a status this list forgot (RESOLVED
  // comes back from the server once rewards commit) would have left the player
  // on a map with no way out.
  if (raid.outcome && raid.status !== null) {
    host.appendChild(resultStrip(raid, wm)); return;
  }
  host.appendChild(launchStrip(wm, raid));
}

/**
 * How much more raid time opens the next zone: "5h 20m", or "3h 33m (Explore)"
 * style text with the Explore speed-up. Null once the map is fully open or
 * before the discovery read lands. Zones open by raid time since 2026-10-19.
 */
export function nextZoneChip(explore: WorldMapState['explore']): string | null {
  if (!explore?.next) return null;
  const left = Math.max(0, explore.next.needMinutes - explore.minutes) * 60_000;
  return `${formatDuration(left)} of raiding · ${formatDuration(left / explore.exploreFocusMult)} exploring`;
}

function launchStrip(wm: WorldMapState, raid: RaidState): HTMLElement {
  const box = document.createElement('div');
  const revealed = wm.regions.filter((r) => r.revealed);

  if (revealed.length === 0) {
    box.appendChild(note('Nothing scouted yet — pick any zone and raid it to start peeling back the fog.'));
  }

  const target = wm.regions.find((r) => r.key === raid.selectedRegion) ?? null;
  const character = gameStateStore.get().character;
  const active = character?.state === 'ACTIVE' ? character : null;

  if (pendingFocus) {
    box.appendChild(confirmStrip(pendingFocus, target, active));
  } else {
    // Same heading the confirmation uses: the zone bold, underlined, in its
    // tier's map colour, with the tier as a coloured badge beside it.
    box.appendChild(raidTitle('Raid', target ? target.label : null, target?.tier ?? 1));
    const zone = nextZoneChip(wm.explore);
    box.appendChild(chipRow(
      ...(target ? [tierChip(target.tier)] : []),
      ...(target && !target.revealed ? [statChip('Status', 'Unscouted', 'warn')] : []),
      ...(zone ? [statChip('Next zone', zone, 'plain', chipIcon('map'))] : []),
    ));
    const row = document.createElement('div');
    row.className = 'bv-act-row bv-wm-focus';
    (['FOOD', 'SCRAP', 'BOUNTY', 'EXPLORE'] as RaidFocusKind[]).forEach((focus) => {
      // Asks first. This used to launch on the click, which spent the whole
      // energy bar on a mis-click with no warning and no way back — and the
      // cost was never written down anywhere the player could see it.
      row.appendChild(focusButton(focus, !raid.selectedRegion, () => {
        if (!raidStateStore.get().selectedRegion) return;
        setPendingFocus(focus);
      }));
    });
    box.appendChild(row);
  }

  if (raid.error) {
    // The same error line every other window uses. It was `.callout
    // callout-danger` here and an inline string elsewhere, which is how one
    // rejected action could look like a warning and another like a note.
    const err = document.createElement('p');
    err.className = 'bv-win-err';
    err.textContent = raid.error;
    box.appendChild(err);
  }

  return box;
}

/**
 * "Raid the Subway District for scrap? It costs 100 energy." — with the
 * arithmetic already done.
 *
 * ─── Why the cost is read, never written ────────────────────────────────────
 * `raidEnergyCost` is `CharacterConfig`'s knob (admin-tunable, and the server
 * charges it in `RaidService.start`), and it has been on the character sheet
 * this whole time with nothing reading it. Printing a literal 100 here would
 * be a second copy of a tunable number, and the kind that goes quietly wrong:
 * an admin raising the cost would leave this prompt lying to the player, and
 * every test would still pass.
 *
 * ─── What it shows when the answer is no ────────────────────────────────────
 * The shortfall, not a dead button with no explanation. `canRaid` comes from
 * the same sheet and is the server's own gate, so a player who cannot afford
 * the raid is told how much energy short they are and how long that is at
 * their regen rate — rather than clicking Launch and receiving
 * INSUFFICIENT_ENERGY, which is what happened before.
 */
function confirmStrip(
  focus: RaidFocusKind,
  target: RegionLayout | null,
  active: CharacterSheetActive | null,
): HTMLElement {
  const box = document.createElement('div');
  box.className = 'well bv-wm-confirm';

  const cost = active
    ? (target ? active.raidEnergyCostByTier[target.tier] : undefined) ?? active.raidEnergyCost
    : 0;
  const energy = active?.energy ?? 0;
  const affordable = active ? energy >= cost : false;

  box.appendChild(raidTitle('Raid', target ? target.label : null, target?.tier ?? 1, ` for ${FOCUS_LABELS[focus].toLowerCase()}?`));

  const energyNow = Math.floor(energy);
  box.appendChild(chipRow(
    ...(target ? [tierChip(target.tier)] : []),
    statChip('Costs', String(cost), affordable ? 'plain' : 'bad', chipIcon('energy')),
    ...(affordable ? [statChip('After', String(energyNow - cost), 'good', chipIcon('energy'))] : []),
  ));
  box.appendChild(energyCostBar(energy, active?.maxEnergy ?? 0, cost));

  const row = document.createElement('div');
  row.className = 'bv-act-row';

  const go = document.createElement('button');
  go.type = 'button';
  go.className = 'bv-act bv-act-go';
  go.textContent = affordable ? 'Go' : 'Not enough energy';
  go.disabled = !affordable;
  go.onclick = () => {
    const region = raidStateStore.get().selectedRegion;
    if (!region) return;
    setPendingFocus(null);
    // Surface failures instead of swallowing them. A silently-dropped rejection
    // is what made this button look dead: INSUFFICIENT_ENERGY, BAG_FULL and
    // RAID_ALREADY_ACTIVE all render as "nothing happened" otherwise.
    void startRaid(focus, region).catch(() => {});
  };

  const no = document.createElement('button');
  no.type = 'button';
  no.className = 'bv-act bv-act-ghost';
  no.textContent = 'Not yet';
  no.onclick = () => setPendingFocus(null);

  row.append(go, no);
  box.appendChild(row);

  if (!affordable && active) {
    // `energyRegenPerMin` is on `stats`, not on the sheet — it is a DERIVED
    // stat (attributes and gear move it), which is exactly why the wait is
    // computed from the player's own rate instead of a constant.
    const missing = cost - energyNow;
    const regen = active.stats.energyRegenPerMin;
    box.appendChild(chipRow(
      statChip('Short', String(missing), 'bad', chipIcon('energy')),
      ...(regen > 0 ? [statChip('Rest', `about ${Math.ceil((cost - energy) / regen)} min`)] : []),
    ));
  }
  return box;
}

export { ceil1 };

function resultStrip(raid: RaidState, wm: WorldMapState): HTMLElement {
  const o = raid.outcome!;
  // Real time out, from the step the run ended on (Janeks, 2026-10-19: raid hours read as real time).
  const outFor = formatDuration((o.endStep + 1) * raid.stepDurationMs);
  const box = document.createElement('div');
  box.className = `bv-result ${o.died ? 'bv-result-died' : 'bv-result-out'}`;

  // The backpack comes home with you, so it sits on the extracted result only,
  // top right, and shifts about when the pointer is over it.
  if (!o.died) box.insertAdjacentHTML('afterbegin', `<span class="bv-result-pack" aria-hidden="true">${chipIcon('pack')}</span>`);

  const h = document.createElement('p');
  // `--terminal` (CRT green) rather than `--moss` for the survived case: moss
  // is a muted olive that reads as "fine", and this line has to land as a
  // result readout next to a death message.
  h.className = `bv-wm-verdict ${o.died ? 'bv-wm-verdict-died' : 'bv-wm-verdict-out'}`;
  if (o.died) h.insertAdjacentHTML('afterbegin', chipIcon('death'));
  h.append(o.died ? 'Died' : 'Extracted');
  box.appendChild(h);

  box.appendChild(o.died
    ? chipRow(
      statChip('Lost after', outFor, 'bad', chipIcon('hours')),
      statChip('Bag', 'lost', 'bad', chipIcon('pack')),
      statChip('Respawn', 'Med-Center'),
    )
    : chipRow(
      statChip('Banked', `${o.itemsBanked} ${o.itemsBanked === 1 ? 'item' : 'items'}`, 'good', chipIcon('pack')),
      statChip('Weight', `${ceil1(o.bagWeight)} kg`, 'plain', chipIcon('weight')),
      statChip('XP', `+${o.xpGranted}`, 'good', chipIcon('xp')),
      statChip('Out for', outFor, 'plain', chipIcon('hours')),
    ));
  // Rules 2: what the player chose to leave at Extract.
  const left = o.leftAtExtract?.length ?? 0;
  if (!o.died && left > 0) {
    box.appendChild(chipRow(statChip('Left behind', `${left} ${left === 1 ? 'find' : 'finds'}`, 'warn')));
  }

  if (o.newlyRevealedRegions.length > 0) {
    // Zone NAMES, in their tier colour, never the raw keys the server uses.
    const n = document.createElement('div');
    n.className = 'bv-notice';
    n.innerHTML = `<span class="bv-notice-head">${chipIcon('map')}New ground revealed</span>`;
    const zones = document.createElement('div');
    zones.className = 'bv-notice-zones';
    for (const key of o.newlyRevealedRegions) {
      const region = wm.regions.find((r) => r.key === key);
      const chip = document.createElement('span');
      chip.className = `bv-chip bv-zone-chip ${tierClass(region?.tier ?? 1)}`;
      chip.textContent = region?.label ?? key.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
      zones.appendChild(chip);
    }
    n.appendChild(zones);
    box.appendChild(n);
  }

  const b = document.createElement('button');
  b.type = 'button';
  // Dismissal is neutral, so it gets the shared recessed treatment rather than
  // a metal gradient — nothing here should compete with Extract for the eye.
  b.className = 'bv-act bv-act-ghost bv-wm-continue';
  b.textContent = 'Continue';
  b.onclick = () => {
    dismissRaidResult();
    // A concluded raid may have revealed regions — refetch so the fog updates
    // without needing a reload.
    void loadWorldMap();
  };
  box.appendChild(b);
  return box;
}

function note(text: string): HTMLElement {
  const p = document.createElement('p');
  p.className = 'bv-win-empty';
  p.textContent = text;
  return p;
}
